import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check } from '../components/Icons.jsx';

const trees = [
  {
    title: 'Diagnostic test accuracy',
    context: 'A screening test for a rare disease. P(disease) = 0.05. P(positive | disease) = 0.90. P(positive | no disease) = 0.10.',
    question: 'What is P(disease AND positive test)?',
    finalAnswer: '0.05 × 0.90 = 0.045',
    finalQuestion: 'If a patient tests positive, what is the probability they actually have the disease? (Bayes)',
    bayesAnswer: '0.045 / (0.045 + 0.095) = 0.045 / 0.14 ≈ 32%',
    bayesNote: 'Low prevalence (5%) means even a 90% sensitive test gives a lot of false positives. This is why mass-population screening for rare diseases is clinically complex.',
    mistake: 'Students forget to calculate all four branches first before computing Bayes. They often just divide 0.90 by (0.90 + 0.10) = 50%, ignoring the prior probability of disease.',
    branches: [
      {
        label: 'Has disease',
        prob: '0.05',
        children: [
          { label: 'Tests positive', prob: '0.90', final: '0.05 × 0.90 = 0.045', highlight: true },
          { label: 'Tests negative', prob: '0.10', final: '0.05 × 0.10 = 0.005' },
        ],
      },
      {
        label: 'No disease',
        prob: '0.95',
        children: [
          { label: 'Tests positive', prob: '0.10', final: '0.95 × 0.10 = 0.095' },
          { label: 'Tests negative', prob: '0.90', final: '0.95 × 0.90 = 0.855' },
        ],
      },
    ],
  },
  {
    title: 'Drug trial dropout',
    context: 'A trial has two arms. P(assigned to Drug A) = 0.5. P(dropout | Drug A) = 0.2. P(dropout | Drug B) = 0.35.',
    question: 'What is P(assigned to Drug B AND drops out)?',
    finalAnswer: '0.5 × 0.35 = 0.175',
    finalQuestion: 'What is the overall probability a randomly selected participant drops out?',
    bayesAnswer: '(0.5 × 0.2) + (0.5 × 0.35) = 0.10 + 0.175 = 0.275',
    bayesNote: 'Total dropout = sum of (P(arm) × P(dropout | arm)) for both arms. This is the law of total probability.',
    mistake: 'Students average the two dropout rates (20% + 35%) / 2 = 27.5% — which only works because both arms have equal probability here. With unequal allocation ratios, this shortcut fails.',
    branches: [
      {
        label: 'Drug A',
        prob: '0.50',
        children: [
          { label: 'Drops out', prob: '0.20', final: '0.50 × 0.20 = 0.10' },
          { label: 'Completes', prob: '0.80', final: '0.50 × 0.80 = 0.40' },
        ],
      },
      {
        label: 'Drug B',
        prob: '0.50',
        children: [
          { label: 'Drops out', prob: '0.35', final: '0.50 × 0.35 = 0.175', highlight: true },
          { label: 'Completes', prob: '0.65', final: '0.50 × 0.65 = 0.325' },
        ],
      },
    ],
  },
  {
    title: 'Compliance and adverse events',
    context: 'P(patient is compliant) = 0.7. P(adverse event | compliant) = 0.05. P(adverse event | non-compliant) = 0.30.',
    question: 'What is the overall probability of an adverse event?',
    finalAnswer: '(0.7 × 0.05) + (0.3 × 0.30) = 0.035 + 0.090 = 0.125',
    finalQuestion: 'Given an adverse event occurred, what is the probability the patient was non-compliant? (Bayes)',
    bayesAnswer: '0.090 / 0.125 = 72%',
    bayesNote: 'Even though non-compliant patients are the minority (30%), they account for the majority of adverse events (72% of all AEs), because their event rate is 6× higher.',
    mistake: 'Students stop after finding the two individual branch probabilities and forget to add them for the total probability of the event.',
    branches: [
      {
        label: 'Compliant',
        prob: '0.70',
        children: [
          { label: 'Adverse event', prob: '0.05', final: '0.70 × 0.05 = 0.035', highlight: true },
          { label: 'No AE', prob: '0.95', final: '0.70 × 0.95 = 0.665' },
        ],
      },
      {
        label: 'Non-compliant',
        prob: '0.30',
        children: [
          { label: 'Adverse event', prob: '0.30', final: '0.30 × 0.30 = 0.090', highlight: true },
          { label: 'No AE', prob: '0.70', final: '0.30 × 0.70 = 0.210' },
        ],
      },
    ],
  },
  {
    title: 'Referral pathway',
    context: 'P(GP refers) = 0.4. P(consultant accepts | GP refers) = 0.75. P(consultant accepts | GP does not refer) = 0 (no self-referral).',
    question: 'What is P(patient reaches consultant)?',
    finalAnswer: '0.4 × 0.75 = 0.30',
    finalQuestion: 'Given a patient reached the consultant, what was the probability the GP referred them? (trivially: 1.0)',
    bayesAnswer: '1.0 — because the only pathway to the consultant is via GP referral in this model.',
    bayesNote: 'When a branch probability is 0, that branch contributes nothing to the total. Students sometimes include it as a "safe" 0 term and then err in the algebra.',
    mistake: 'Students multiply the wrong branches or confuse P(referred AND accepted) with P(accepted | referred).',
    branches: [
      {
        label: 'GP refers',
        prob: '0.40',
        children: [
          { label: 'Consultant accepts', prob: '0.75', final: '0.40 × 0.75 = 0.30', highlight: true },
          { label: 'Consultant declines', prob: '0.25', final: '0.40 × 0.25 = 0.10' },
        ],
      },
      {
        label: 'GP does not refer',
        prob: '0.60',
        children: [
          { label: 'Reaches consultant', prob: '0.00', final: '0.60 × 0 = 0' },
          { label: 'Does not reach', prob: '1.00', final: '0.60 × 1.00 = 0.60' },
        ],
      },
    ],
  },
];

export default function TutorProbLive() {
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState({});
  const [showAnswer, setShowAnswer] = useState(false);
  const [showBayes, setShowBayes] = useState(false);
  const [showMistake, setShowMistake] = useState(false);

  const t = trees[idx];

  const next = () => {
    setIdx((i) => (i + 1) % trees.length);
    setRevealed({});
    setShowAnswer(false);
    setShowBayes(false);
    setShowMistake(false);
  };

  const revealKey = (branchI, childI) => `${branchI}-${childI}`;
  const reveal = (key) => setRevealed((r) => ({ ...r, [key]: true }));
  const allRevealed = t.branches.every((b, bi) => b.children.every((_, ci) => revealed[revealKey(bi, ci)]));

  return (
    <>
      <Link to="/tutor-portal" className="back-link"><ArrowLeft width="16" height="16" />Back to tutor portal</Link>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Tutor Portal · Probability Live</div>
        <h1>Step-through probability trees.</h1>
        <p className="subtitle">Tap each branch to reveal probabilities one at a time. Students calculate as you go — the answer stays hidden until you're ready.</p>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Tree {idx + 1} of {trees.length}</span>
        <button onClick={next} className="btn btn-primary" style={{ marginLeft: 'auto' }}>Next tree <ArrowRight width="14" height="14" /></button>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: '20px 24px', background: 'var(--card-soft)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{t.title}</div>
        <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: 'var(--text)' }}>{t.context}</p>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: '20px 24px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Probability tree — tap to reveal each branch</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {t.branches.map((branch, bi) => (
            <div key={bi} style={{ display: 'flex', gap: 0, alignItems: 'stretch' }}>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', marginRight: 12 }}>
                <BranchChip label={branch.label} prob={branch.prob} />
              </div>
              <div style={{ borderLeft: '2px solid var(--border)', paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                {branch.children.map((child, ci) => {
                  const key = revealKey(bi, ci);
                  const isRevealed = revealed[key];
                  return (
                    <div key={ci} style={{
                      padding: '12px 16px', borderRadius: 10, cursor: isRevealed ? 'default' : 'pointer',
                      border: `1px solid ${isRevealed && child.highlight ? 'var(--brand-teal)' : isRevealed ? 'var(--border)' : '#d8d6e6'}`,
                      background: isRevealed && child.highlight ? '#ecfdf5' : isRevealed ? '#f9f7ff' : '#fff',
                      transition: 'all 0.15s',
                    }}
                      onClick={() => !isRevealed && reveal(key)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{child.label}</span>
                        {isRevealed
                          ? <span style={{ fontSize: 14, fontWeight: 700, color: child.highlight ? 'var(--brand-teal)' : 'var(--brand-purple)', fontVariantNumeric: 'tabular-nums' }}>{child.prob}</span>
                          : <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>tap to reveal</span>}
                      </div>
                      {isRevealed && (
                        <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                          Branch probability: <strong style={{ color: child.highlight ? 'var(--brand-teal)' : 'var(--text)' }}>{child.final}</strong>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {allRevealed && !showAnswer && (
        <div className="card" style={{ marginBottom: 16, padding: '18px 22px', background: 'var(--card-soft)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Question</div>
          <p style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{t.question}</p>
          <button onClick={() => setShowAnswer(true)} className="btn btn-primary">
            <Check width="14" height="14" /> Reveal answer
          </button>
        </div>
      )}

      {showAnswer && (
        <>
          <div className="card" style={{ marginBottom: 14, padding: '18px 22px', borderLeft: '4px solid var(--live-green)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Answer</div>
            <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>{t.finalAnswer}</p>
          </div>

          {!showBayes ? (
            <button onClick={() => setShowBayes(true)} className="btn" style={{ marginBottom: 14 }}>
              Extension: {t.finalQuestion}
            </button>
          ) : (
            <div className="card" style={{ marginBottom: 14, padding: '18px 22px', borderLeft: '4px solid var(--brand-purple)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-deep)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Extension answer</div>
              <p style={{ margin: '0 0 8px', fontSize: 14.5, fontWeight: 700, color: 'var(--text)' }}>{t.bayesAnswer}</p>
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.55 }}>{t.bayesNote}</p>
            </div>
          )}

          {!showMistake ? (
            <button onClick={() => setShowMistake(true)} className="btn" style={{ marginBottom: 14 }}>
              Show common student mistake
            </button>
          ) : (
            <div className="card" style={{ marginBottom: 16, padding: '16px 20px', background: '#fffbeb', borderLeft: '4px solid #f59e0b' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Common mistake</div>
              <p style={{ margin: 0, fontSize: 13.5, color: '#78350f', lineHeight: 1.55 }}>{t.mistake}</p>
            </div>
          )}
        </>
      )}
    </>
  );
}

function BranchChip({ label, prob }) {
  return (
    <div style={{ background: '#f3eefc', borderRadius: 10, padding: '10px 14px', textAlign: 'center', minWidth: 100 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-deep)' }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--brand-purple)', marginTop: 3 }}>p = {prob}</div>
    </div>
  );
}
